import {Button, DatePicker, Divider, Flex, Form, Input, InputNumber, Spin, Table, Tooltip, Typography} from 'antd';
import Search from 'antd/es/input/Search';
import {ColumnsType} from 'antd/lib/table';
import dayjs from 'dayjs';
import React, {useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {ApiQuery} from '../../../gen/api';
import {useAdminPalletOrderLookupMutation} from '../../../gen/api/apis';
import {AdminPalletOrder, AdminPalletOrderAttachments, AdminPalletOrderLookupItem} from '../../../gen/api/models';
import {useNotification} from '../../../NotificationContext';
import {Attachments} from './components/Attachments';

const {Text, Title} = Typography;

export const PalletOrderDetailPage: React.FC = () => {
    const {id} = useParams();
    if (!id) {
        return <>Page not found</>;
    }

    const navigate = useNavigate();
    const [form] = Form.useForm();
    const notify = useNotification();

    const [error, setError] = React.useState<string | null>(null);
    const [attachments, setAttachments] = React.useState<Array<AdminPalletOrderAttachments>>([]);
    const [search, setSearch] = React.useState<string>('');

    const palletQuery = ApiQuery.useAdminGetPalletMutation({
        onSuccess(data) {
            form.setFieldsValue({
                items: data.items,
                junkItems: data.junkItems,
                name: data.name,
                orderDate: dayjs(data.orderDate),
                price: data.price,
                orderLink: data.orderLink,
            });
            setAttachments(data.attachments || []);
        },
        onError() {
            setError('Something went wrong');
        },
    });

    React.useEffect(() => {
        palletQuery.mutate({
            id: parseInt(id),
        });
    }, [id]);

    const update = ApiQuery.useAdminUpdatePalletMutation({
        onSuccess() {
            notify.openNotificationWithIcon('success', 'Pallet order created');
            navigate('/pallet-orders/');
        },
        onError() {
            setError('Something went wrong');
        },
    });

    const handleSubmit = () => {
        console.log('form', form.getFieldsValue());

        setError(null);
        update.mutate({
            id: id ? parseInt(id) : 0,
            params: {
                items: form.getFieldValue('items'),
                junkItems: form.getFieldValue('junkItems'),
                name: form.getFieldValue('name'),
                orderDate: form.getFieldValue('orderDate'),
                price: form.getFieldValue('price'),
                orderLink: form.getFieldValue('orderLink'),
            },
        });
    };

    Form.useWatch((values) => values, form);

    const checkDiff = (baseOrder: AdminPalletOrder) => {
        const diff = {
            items: baseOrder.items !== form.getFieldValue('items'),
            junkItems: baseOrder.junkItems !== form.getFieldValue('junkItems'),
            name: baseOrder.name !== form.getFieldValue('name'),
            orderDate: !dayjs(baseOrder.orderDate).isSame(dayjs(form.getFieldValue('orderDate'))),
            price: baseOrder.price !== form.getFieldValue('price'),
            orderLink: baseOrder.orderLink !== form.getFieldValue('orderLink'),
        };

        return Object.values(diff).some(Boolean);
    };

    const handleUploadAttachment = async () => {
        await palletQuery.mutateAsync(
            {
                id: parseInt(id),
            },
            {
                onSuccess(data) {
                    setAttachments(data.attachments || []);
                },
            }
        );
    };

    const [searchResult, setSearchResult] = React.useState<Array<AdminPalletOrderLookupItem>>([]);

    const lookupProductRequest = useAdminPalletOrderLookupMutation({
        onSuccess: (data) => {
            if (data.items.find((item) => item.name === form.getFieldValue('name'))) {
                setSearchResult([]);
                return;
            }
            setSearchResult(data.items);
        },
        onError: () => {
            notify.openNotificationWithIcon('error', 'Failed to fetch suggested products');
        },
    });

    useEffect(() => {
        if (search) {
            const delayDebounceFn = setTimeout(() => {
                lookupProductRequest.mutate({
                    id: parseInt(id),
                    name: search || '',
                });
            }, 500);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [search]);

    const searchTableColumns: ColumnsType<AdminPalletOrderLookupItem> = [
        {
            title: 'ASIN',
            dataIndex: 'asin',
            key: 'name',
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (asin) => (
                <Tooltip
                    placement="topLeft"
                    title={asin}
                >
                    {asin}
                </Tooltip>
            ),
        },

        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: 60,
            ellipsis: true,
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 100,
            ellipsis: true,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            ellipsis: {
                showTitle: false,
            },
            render: (name) => (
                <Tooltip
                    placement="topLeft"
                    title={name}
                >
                    {name}
                </Tooltip>
            ),
        },
    ];

    return (
        <div>
            {palletQuery.isLoading ? (
                <Spin />
            ) : (
                <>
                    <Title
                        level={3}
                        style={{marginTop: 0}}
                    >
                        {palletQuery.data?.name}
                    </Title>
                    <Form
                        onFinish={handleSubmit}
                        disabled={update.isLoading}
                        labelCol={{span: 4}}
                        wrapperCol={{span: 20}}
                        style={{maxWidth: 800}}
                        form={form}
                    >
                        <Form.Item
                            label="Name"
                            name="name"
                            rules={[{required: true, message: 'Please input name!'}]}
                        >
                            <Input placeholder="name" />
                        </Form.Item>
                        <Form.Item
                            label="Order Date"
                            name="orderDate"
                            rules={[{required: true, message: 'Please input order date!'}]}
                        >
                            <DatePicker />
                        </Form.Item>
                        <Form.Item
                            label="Items"
                            name="items"
                            rules={[{required: true, message: 'Please input items!'}]}
                        >
                            <InputNumber />
                        </Form.Item>
                        <Form.Item
                            label="Junk Items"
                            name="junkItems"
                            rules={[{required: true, message: 'Please input junk items!'}]}
                        >
                            <InputNumber />
                        </Form.Item>
                        <Form.Item
                            label="Price"
                            name="price"
                            rules={[{required: true, message: 'Please input price!'}]}
                        >
                            <InputNumber />
                        </Form.Item>
                        <Form.Item
                            label="Order Link"
                            name="orderLink"
                            rules={[{required: true, message: 'Please input order link!'}]}
                        >
                            <Input />
                        </Form.Item>

                        {error && <Text type={'danger'}>{error}</Text>}
                        <Flex justify={'end'}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                disabled={palletQuery.data ? !checkDiff(palletQuery.data) : false}
                                loading={update.isLoading}
                            >
                                Update
                            </Button>
                        </Flex>
                    </Form>
                    <Divider />
                    {palletQuery.data && (
                        <Attachments
                            orderId={palletQuery.data?.id!}
                            attachments={attachments}
                            onSuccess={handleUploadAttachment}
                        />
                    )}
                    <Divider />
                    <Flex justify={'end'}>
                        <Search
                            placeholder={'Search by Name/ASIN'}
                            onSearch={(value) => {
                                setSearch(value);
                            }}
                            enterButton
                            size={'middle'}
                            style={{width: 300, marginBottom: 16}}
                            allowClear
                            onClear={() => setSearch('')}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                            }}
                        />
                    </Flex>
                    <Table
                        dataSource={searchResult}
                        columns={searchTableColumns}
                        size={'small'}
                        pagination={false}
                    />
                </>
            )}
        </div>
    );
};
